// ══════════════════════════════════════════════════════
//  PIEZAS COMUNES DE LAS PANTALLAS DE CUENTA (login, registro, contraseñas, email, perfil)
//  - rutaSegura: solo rutas internas para volver tras iniciar sesión (anti open redirect)
//  - mensajeError: texto legible para un error de axios (sin romper si no hay respuesta)
//  - CampoPassword: contraseña con botón «Mostrar/Ocultar» (aria-pressed)
//  - useCuentaAtras: espera de 60 s entre envíos (verificación y recuperar contraseña)
//  - RequisitosPassword: requisitos en vivo, aviso de 72 bytes y fuerza
//  - ReenviarVerificacion: botón «Reenviar el email» con espera entre envíos
//  - PaginaCuenta, Alerta, ErrorCampo, CampoTexto, IconoEstado: piezas visuales comunes
//  Estilos: styles/Auth/Cuenta.css (clases con prefijo cu-)
// ══════════════════════════════════════════════════════
import { useEffect, useRef, useState } from "react";
import { PiCheck, PiCheckCircle, PiCircle, PiEye, PiEyeSlash, PiInfo, PiWarning, PiWarningCircle } from "react-icons/pi";
import { API } from "../../services/api";
import { PORTADA } from "../../config/media";
import {
  MENSAJE_PASSWORD_BYTES,
  NOTA_PASSWORD,
  REQUISITOS_PASSWORD,
  fuerzaPassword,
  superaBytes,
} from "../../services/password";
import "../../styles/Auth/Cuenta.css";

export const TITULO_WEB = "Plato tras Plato";

// Solo rutas de esta web («/recipes/3»): nada de «//otro-sitio.com», «/\otro.com» ni URLs completas
export const rutaSegura = (ruta) =>
  typeof ruta === "string" && ruta.startsWith("/") && !ruta.startsWith("//") && !ruta.startsWith("/\\")
    ? ruta
    : null;

export const MENSAJE_SIN_CONEXION =
  "No se pudo conectar con el servidor. Comprueba tu conexión e inténtalo de nuevo.";
export const MENSAJE_LIMITE = "Demasiados intentos. Espera unos minutos y vuelve a intentarlo.";
export const MENSAJE_SERVIDOR = "Ha fallado algo en el servidor. Inténtalo de nuevo en unos minutos.";

// «90 s» → «2 minutos»; para decirle al usuario cuánto esperar
export const textoEspera = (segundos) => {
  if (!Number.isFinite(segundos) || segundos <= 0) return "";
  if (segundos < 90) return `${Math.ceil(segundos)} segundos`;
  const minutos = Math.ceil(segundos / 60);
  return minutos >= 90 ? `${Math.ceil(minutos / 60)} horas` : `${minutos} minutos`;
};

// Segundos de la cabecera Retry-After (desde otro dominio solo se ve si la API la expone
// con CORS exposedHeaders; si no, devuelve 0 y se usa el texto del mensaje de la API)
export const segundosDeEspera = (error) => {
  const cabecera = error?.response?.headers?.["retry-after"];
  const n = Number.parseInt(cabecera, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// Texto legible para un error de axios. «porDefecto» para los 400 sin mensaje.
export const mensajeError = (error, porDefecto) => {
  const status = error?.response?.status;
  if (!status) return MENSAJE_SIN_CONEXION;
  const msg = error.response.data?.message;
  if (status === 429) {
    const espera = textoEspera(segundosDeEspera(error));
    const base = (typeof msg === "string" && msg) || MENSAJE_LIMITE;
    return espera ? `${base} (Espera ${espera}.)` : base;
  }
  if (status >= 500) return MENSAJE_SERVIDOR;
  return (typeof msg === "string" && msg) || porDefecto;
};

// ── Piezas visuales comunes ─────────────────────────────
// Página de cuenta: suelo azul suave o, con «foto», la foto de portada (Cloudinary, sin
// dependencias externas) con un velo neutro. El panel va dentro, como hijo.
export function PaginaCuenta({ foto = false, className = "", children }) {
  return (
    <div className={`cu-pagina${foto ? " cu-pagina--foto" : ""}${className ? ` ${className}` : ""}`}>
      {foto && (
        <picture className="cu-fondo">
          <source media="(min-width: 768px)" srcSet={PORTADA.fotoEscritorio} />
          <img src={PORTADA.fotoMovil} alt="" decoding="async" />
        </picture>
      )}
      {children}
    </div>
  );
}

// Cuadro de aviso: tipo = error | ok | info | aviso. El rol lo decide quien lo usa
// (alert para errores, status para éxito e información).
const ICONOS_ALERTA = { error: PiWarningCircle, ok: PiCheckCircle, info: PiInfo, aviso: PiWarning };

export function Alerta({ tipo = "info", role, id, children }) {
  const Icono = ICONOS_ALERTA[tipo] || PiInfo;
  return (
    <div id={id} className={`cu-aviso cu-aviso--${tipo}`} role={role}>
      <Icono aria-hidden="true" />
      <div className="cu-aviso__texto">{children}</div>
    </div>
  );
}

// Error de un campo: role=alert y un id para el aria-describedby del campo
export function ErrorCampo({ id, children }) {
  return (
    <p id={id} className="cu-error" role="alert">
      <PiWarningCircle aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

// Campo de texto con etiqueta visible, ayuda opcional y error por campo.
// «como» = "textarea" para un área de texto. El resto de props van al control.
export function CampoTexto({ id, etiqueta, valor, onChange, error, ayuda, inputRef, como = "input", ...resto }) {
  const Control = como;
  const describedby = [ayuda ? `${id}-ayuda` : null, error ? `${id}-error` : null].filter(Boolean).join(" ");
  return (
    <div className="cu-campo">
      <label htmlFor={id} className="cu-campo__etiqueta">
        {etiqueta}
      </label>
      <Control
        ref={inputRef}
        id={id}
        className="cu-input"
        value={valor}
        onChange={onChange}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedby || undefined}
        {...resto}
      />
      {ayuda && (
        <p id={`${id}-ayuda`} className="cu-ayuda">
          {ayuda}
        </p>
      )}
      {error && <ErrorCampo id={`${id}-error`}>{error}</ErrorCampo>}
    </div>
  );
}

// Círculo con icono para las pantallas de estado. tipo = ok (con un pulso azul) | error
export function IconoEstado({ tipo = "ok", icono: Icono }) {
  return (
    <div className={`cu-icono${tipo === "error" ? " cu-icono--error" : " cu-icono--pulso"}`} aria-hidden="true">
      <Icono />
    </div>
  );
}

// ── Contraseña con mostrar/ocultar ──────────────────────
export function CampoPassword({
  id,
  name = "password",
  etiqueta,
  valor,
  onChange,
  onBlur,
  autoComplete,
  descripcion,
  invalido,
  inputRef,
  requerido = true,
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="cu-campo">
      <label htmlFor={id} className="cu-campo__etiqueta">
        {etiqueta}
      </label>
      <div className="cu-password">
        <input
          ref={inputRef}
          id={id}
          name={name}
          type={visible ? "text" : "password"}
          className="cu-input"
          value={valor}
          onChange={onChange}
          onBlur={onBlur}
          autoComplete={autoComplete}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          maxLength={128}
          required={requerido}
          aria-invalid={invalido ? true : undefined}
          aria-describedby={descripcion || undefined}
        />
        <button
          type="button"
          className="cu-password__ver"
          aria-pressed={visible}
          aria-controls={id}
          title={visible ? "Ocultar la contraseña" : "Mostrar la contraseña"}
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? <PiEyeSlash aria-hidden="true" /> : <PiEye aria-hidden="true" />}
          <span className="solo-lectores">{visible ? "Ocultar" : "Mostrar"} la contraseña</span>
        </button>
      </div>
    </div>
  );
}

// ── Cuenta atrás entre envíos ───────────────────────────
// Devuelve [segundos que quedan, empezar(segundos)]. Baja de 1 en 1 cada segundo.
export const ESPERA_SEGUNDOS = 60;

export function useCuentaAtras(inicial = 0) {
  const [espera, setEspera] = useState(inicial);
  const temporizador = useRef(null);
  useEffect(() => {
    if (espera <= 0) return undefined;
    temporizador.current = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(temporizador.current);
  }, [espera]);
  return [espera, setEspera];
}

// ── Requisitos y fuerza de una contraseña nueva ─────────
// Lista en vivo (mismos textos que la API), aviso de los 72 bytes y barra de fuerza.
// idBase: prefijo de los id («reg-password» → reg-password-requisitos, -fuerza, -bytes)
export function RequisitosPassword({ idBase, password }) {
  const fuerza = fuerzaPassword(password);
  return (
    <>
      <ul id={`${idBase}-requisitos`} className="cu-requisitos" aria-label="Requisitos de la contraseña">
        {REQUISITOS_PASSWORD.map((r) => {
          const ok = r.cumple(password);
          return (
            <li key={r.id} className={ok ? "cu-requisitos__ok" : undefined}>
              {ok ? <PiCheck aria-hidden="true" /> : <PiCircle aria-hidden="true" />}
              <span>
                {r.texto}
                <span className="solo-lectores">{ok ? " (cumplido)" : " (pendiente)"}</span>
              </span>
            </li>
          );
        })}
        <li className="cu-requisitos__nota">{NOTA_PASSWORD}</li>
      </ul>
      {superaBytes(password) && (
        <p id={`${idBase}-bytes`} className="cu-error">
          <PiWarningCircle aria-hidden="true" />
          <span>{MENSAJE_PASSWORD_BYTES}</span>
        </p>
      )}
      <div className="cu-fuerza">
        <div className="cu-fuerza__pista" aria-hidden="true">
          <div className={`cu-fuerza__barra cu-fuerza__barra--${fuerza.nivel}`} style={{ width: `${fuerza.nivel * 25}%` }} />
        </div>
        <p id={`${idBase}-fuerza`} className="cu-fuerza__texto">
          {password ? `Fuerza: ${fuerza.texto}` : ""}
        </p>
      </div>
    </>
  );
}

// id de los textos que describen una contraseña nueva (para aria-describedby)
export const describirPassword = (idBase, password, conError) =>
  [`${idBase}-requisitos`, superaBytes(password) ? `${idBase}-bytes` : null, `${idBase}-fuerza`, conError ? `${idBase}-error` : null]
    .filter(Boolean)
    .join(" ");

// ── Reenviar el email de verificación ───────────────────
// La API responde siempre lo mismo (no dice si el email existe). Entre envíos
// hay que esperar 60 s; la API además limita los reenvíos (429).
export function ReenviarVerificacion({ email, esperaInicial = 0 }) {
  const [estado, setEstado] = useState("listo"); // listo | enviando | enviado | error
  const [mensaje, setMensaje] = useState("");
  const [espera, setEspera] = useCuentaAtras(esperaInicial);

  const reenviar = async () => {
    if (estado === "enviando" || espera > 0 || !email) return;
    setEstado("enviando");
    setMensaje("");
    try {
      const { data } = await API.post("/users/resend-verification", { email }, { timeout: 30000 });
      setEstado("enviado");
      setMensaje(
        data?.message || "Si el email está registrado y sin verificar, te hemos enviado un nuevo enlace.",
      );
      setEspera(ESPERA_SEGUNDOS);
    } catch (error) {
      setEstado("error");
      setMensaje(mensajeError(error, "No se pudo reenviar el email. Inténtalo de nuevo."));
      // Tras un 429 también se espera antes de poder volver a pulsar
      if (error?.response?.status === 429) setEspera(ESPERA_SEGUNDOS);
    }
  };

  const bloqueado = estado === "enviando" || espera > 0;
  return (
    <div className="cu-reenvio">
      {/* aria-disabled (no disabled): el botón sigue teniendo el foco mientras espera */}
      <button
        type="button"
        className={`cu-btn cu-btn--secundario${estado === "enviando" ? " cu-btn--carga" : ""}`}
        onClick={reenviar}
        aria-disabled={bloqueado}
        aria-describedby="cu-reenvio-espera"
      >
        {estado === "enviando" ? "Reenviando…" : "Reenviar el email"}
      </button>
      {/* role=status: el lector de pantalla anuncia el resultado sin mover el foco */}
      <p
        role="status"
        className={`cu-reenvio__estado${estado === "error" ? " cu-reenvio__estado--error" : ""}`}
      >
        {mensaje}
      </p>
      {/* La cuenta atrás va fuera de la región viva: si no, se anunciaría cada segundo */}
      <p id="cu-reenvio-espera" className="cu-reenvio__espera">
        {espera > 0 ? `Podrás pedir otro en ${espera} s.` : ""}
      </p>
    </div>
  );
}
